import { FavoriteType } from '@griply/types';
import { taskListFilters, taskListGroupOptions } from '@griply/utils';
import { Skeleton } from '@mui/material';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import {
  getDefaultFilterDocument,
  getFilterDocument,
  useSortedTags,
  useTagById,
  useUpdateTag,
} from 'features/tags';
import { useIsFavorite, useToggleFavorite } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import { SettingsSort18, Star18 } from 'shared/assets/icons';
import { TagContextMenu } from 'shared/components/connected/tag-context-menu';
import { FilterButton } from 'shared/components/ui/filter-button';
import { IconButton } from 'shared/components/ui/icon-button';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './page-top-bar.style';

export const LoadingInteractions: React.FC = () => (
  <Skeleton animation="wave" width="6.4rem" height="3.2rem" />
);

export const Interactions: React.FC = () => {
  const interactionsRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);

  const { id } = useParams();
  const isFavorite = useIsFavorite(id);
  const tag = useTagById(id);
  const { submit: updateTag } = useUpdateTag();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const tags = useSortedTags();

  const {
    menuOpen: settingsMenuOpen,
    openMenu: openSettingsMenu,
    closeMenu: closeSettingsMenu,
  } = useOpenMenu();
  const toggleFavorite = useToggleFavorite();

  const onFavorite = () => tag && toggleFavorite(tag.id, FavoriteType.Tag);

  const filterDocument = useMemo(() => getFilterDocument(tag), [tag]);

  const onUpdateFilterDocument = useCallback(
    (newFilterDocument: typeof filterDocument) => {
      if (!tag) {
        return;
      }
      updateTag({
        ...tag,
        filterDocument: newFilterDocument,
      });
    },
    [tag, updateTag],
  );

  const onResetFilter = useCallback(() => {
    if (!tag) {
      return;
    }
    updateTag({
      ...tag,
      filterDocument: getDefaultFilterDocument(tag),
    });
  }, [tag, updateTag]);

  if (!tag) {
    return null;
  }

  return (
    <Styled.Interactions ref={interactionsRef}>
      <Styled.FavoriteButtonContainer $isFavorite={isFavorite}>
        <IconButton icon={Star18} onClick={onFavorite} preventDrag />
      </Styled.FavoriteButtonContainer>

      <FilterButton
        filterDocument={filterDocument}
        defaultFilterDocument={getDefaultFilterDocument(tag)}
        updateFilterDocument={onUpdateFilterDocument}
        onReset={onResetFilter}
        tags={tags}
        goals={goals}
        lifeAreas={lifeAreas}
        supportedGroupByModes={taskListGroupOptions}
        supportedFilters={taskListFilters}
        renderAsIconButton
        preventDrag
      />

      <IconButton
        ref={moreRef}
        icon={SettingsSort18}
        onClick={openSettingsMenu}
        preventDrag
      />

      {settingsMenuOpen && (
        <TagContextMenu
          id={id!}
          onClose={closeSettingsMenu}
          referenceElement={moreRef}
        />
      )}
    </Styled.Interactions>
  );
};
